import { useState, type ReactElement } from 'react';
import type { BotDifficulty, LobbyPlayer, ReactionId, SeatCount } from '@big2/shared';
import { useI18n } from '../../i18n.js';
import { ReactionPicker } from '../ReactionUI.js';
import type { ActiveReaction } from '../../net/socket/types.js';
import { PlayerProfileModal } from '../profile/PlayerProfileModal.js';
import { arrangeLobbySeats } from '../../utils/lobbySeats.js';
import { LobbySeatTable } from './LobbySeatTable.js';
import { LobbySeatDetail } from './LobbySeatDetail.js';
import type { LobbyStatsContext } from './lobbySeatStats.js';

interface FriendsData {
  readonly friends: readonly { userId: string }[];
  readonly outgoingRequests: readonly { toUserId: string }[];
}

interface LobbySeatsProps {
  readonly players: LobbyPlayer[];
  readonly seats: SeatCount;
  readonly fillWithBots: boolean;
  readonly botDifficulty?: BotDifficulty;
  readonly myPlayerId: string | null;
  readonly stats: LobbyStatsContext;
  readonly reactions: Record<string, ActiveReaction>;
  readonly friendsData?: FriendsData | null;
  readonly myUserId?: string;
  readonly isHost?: boolean;
  readonly onSendFriendRequest?: (params: { toUserId: string }) => void;
  readonly onOpenProfile?: () => void;
  readonly onInviteClick?: () => void;
  readonly onSendReaction?: (reactionId: ReactionId) => void;
  readonly onKickPlayer?: (playerId: string) => void;
  readonly onChooseSeat?: (seat: number, playerId?: string) => void;
}

const SEAT_HINTS = {
  host: {
    en: 'Tap a player to see details or move them. Tap an empty seat to sit there.',
    mn: 'Тоглогч дээр дарж мэдээлэл харах эсвэл суудлыг нь солино. Хоосон суудал дээр дарж сууна.',
  },
  guest: {
    en: 'Tap a player to see details, or an empty seat to sit there.',
    mn: 'Тоглогч дээр дарж мэдээлэл харна. Хоосон суудал дээр дарж сууна.',
  },
} as const;

const EMPTY_SEAT_LABELS = {
  bot: { en: 'Bot', mn: 'Бот' },
  open: { en: 'Open', mn: 'Нээлттэй' },
} as const;

function friendStateOf(player: LobbyPlayer, myUserId: string | undefined, data: FriendsData | null | undefined): 'none' | 'friend' | 'pending' | null {
  if (!myUserId || !player.userId || player.userId === myUserId) return null;
  if (data?.friends.some((f) => f.userId === player.userId)) return 'friend';
  if (data?.outgoingRequests.some((r) => r.toUserId === player.userId)) return 'pending';
  return 'none';
}

/** The lobby roster: one table that shows who sits where, their standing, and opens per-player actions. */
export function LobbySeats({
  players, seats, fillWithBots, botDifficulty, myPlayerId, stats, reactions, friendsData, myUserId,
  isHost = false, onSendFriendRequest, onOpenProfile, onInviteClick, onSendReaction, onKickPlayer, onChooseSeat,
}: LobbySeatsProps): ReactElement {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [inspected, setInspected] = useState<{ userId: string; name: string } | null>(null);

  const slots = arrangeLobbySeats(players, seats);
  const selectedSeat = slots.findIndex((p) => !!p && p.playerId === selectedId);
  const selected = selectedSeat >= 0 ? slots[selectedSeat] : null;
  const moving = isMoving && !!selected;
  const openSeats = slots.filter((p) => !p).length;

  const closeDetail = (): void => {
    setSelectedId(null);
    setIsMoving(false);
  };

  const handleSeatClick = (seat: number, occupant: LobbyPlayer | null): void => {
    if (moving && selected) {
      if (occupant?.playerId === selected.playerId) {
        setIsMoving(false);
        return;
      }
      onChooseSeat?.(seat, selected.playerId === myPlayerId ? undefined : selected.playerId);
      closeDetail();
      return;
    }
    if (occupant) {
      setIsMoving(false);
      setSelectedId((current) => (current === occupant.playerId ? null : occupant.playerId));
      return;
    }
    onChooseSeat?.(seat);
    closeDetail();
  };

  const hint = SEAT_HINTS[isHost ? 'host' : 'guest'][isMn ? 'mn' : 'en'];
  const emptySeatLabel = EMPTY_SEAT_LABELS[fillWithBots ? 'bot' : 'open'][isMn ? 'mn' : 'en'];

  const renderDetail = (player: LobbyPlayer): ReactElement => {
    const isSelf = player.playerId === myPlayerId;
    const canViewProfile = isSelf ? !!(myUserId && onOpenProfile) : !!player.userId;
    return (
      <LobbySeatDetail
        player={player}
        seatNumber={selectedSeat + 1}
        isSelf={isSelf}
        botDifficulty={botDifficulty}
        stats={stats}
        friendState={friendStateOf(player, myUserId, friendsData)}
        isMoving={moving}
        onClose={moving ? () => setIsMoving(false) : closeDetail}
        onViewProfile={canViewProfile
          ? () => (isSelf ? onOpenProfile?.() : setInspected({ userId: player.userId!, name: player.name }))
          : undefined}
        onAddFriend={onSendFriendRequest && player.userId
          ? () => onSendFriendRequest({ toUserId: player.userId! })
          : undefined}
        onMove={isHost && onChooseSeat && !player.isBot ? () => setIsMoving(true) : undefined}
        onKick={isHost && onKickPlayer && !isSelf && !stats.isFinished
          ? () => {
              onKickPlayer(player.playerId);
              closeDetail();
            }
          : undefined}
      />
    );
  };

  return (
    <section className="lobby-seats-section">
      <div className="lobby-section-title-bar">
        <h3>
          <span className="lobby-section-suit" aria-hidden="true">♠</span>
          <span>{t.playersHeader(players.length, seats)}</span>
        </h3>
        {onSendReaction && (
          <ReactionPicker onSelect={onSendReaction} onOpenProfile={onOpenProfile} direction="down" compact />
        )}
      </div>

      <div className="lobby-seat-picker">
        <LobbySeatTable
          slots={slots}
          myPlayerId={myPlayerId}
          selectedId={selected?.playerId ?? null}
          isMoving={moving}
          canTakeEmptySeat={!!onChooseSeat}
          emptySeatLabel={emptySeatLabel}
          reactions={reactions}
          stats={stats}
          onSeatClick={handleSeatClick}
        />
        {selected ? renderDetail(selected) : <p className="lobby-seat-picker-hint">{hint}</p>}
        {!fillWithBots && openSeats > 0 && onInviteClick && (
          <button type="button" className="lobby-empty-action" onClick={onInviteClick}>
            + {isMn ? 'Найзаа урих' : 'Invite a friend'}
          </button>
        )}
      </div>

      {inspected && (
        <PlayerProfileModal
          userId={inspected.userId}
          initialPlayerName={inspected.name}
          onClose={() => setInspected(null)}
        />
      )}
    </section>
  );
}
